import { useMemo } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { jobUserDataAtom, jobCostDetailAtom } from '../../store/company';

// Shared per-phase performance data for the Work-tab Gantt and the phase
// time-card cards.
//
//   actualHours      — JDE labor hours per phase per day (51x cost types, 518x
//                      excluded — same filter as the Productivity tab).
//   fpQty            — HeavyJob installed quantities per phase per day
//                      (Document_Type 'FP' rows carry qty in Total_Hours). Most
//                      jobs have none — quantities then come only from the
//                      daily reports.
//   phasedHours      — budgeted daily crew hours per phase from the phased Burn
//                      Tool rows (Number_Hours/wk per person × headcount ÷ 7).
//   trackerInstalled — the installed quantity typed on the Productivity
//                      Tracker (Scorecard 'installed' per phase). A scalar per
//                      phase, not per-day — it's the PM's to-date count from
//                      the last forecast, used as a floor under the
//                      field-report install history.

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const dateOf = (v) => (v ? moment(v).add(6, 'hours').format('YYYY-MM-DD') : null);

// The day the work actually happened. Consolidated_Date is the JDE work date;
// GL_Date is the week-ending date the batch posted under, so reading GL_Date
// piles a whole week of hours onto one Sunday and leaves every other day
// empty. Consolidated_Date first, GL_Date only as the fallback.
const workDay = (r) => String(r?.Consolidated_Date || r?.Service_Date || r?.GL_Date || '').slice(0, 10);

export const usePhaseMetrics = () => {
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);

    const { actualHours, fpQty } = useMemo(() => {
        const hours = {};
        const qty = {};
        (jobCostDetail || []).forEach((r) => {
            const code = String(r?.Phase_Code || '').trim();
            const v = num(r?.Total_Hours);
            const date = workDay(r);
            if (!code || !v || !date) return;
            if (String(r?.Document_Type) === 'FP') {
                const byDay = (qty[code] = qty[code] || {});
                byDay[date] = (byDay[date] || 0) + v;
                return;
            }
            const ct = String(r?.Cost_Type_Code || '');
            if (!(ct[0] === '5' && ct[1] === '1' && ct[2] !== '8')) return;
            const byDay = (hours[code] = hours[code] || {});
            byDay[date] = (byDay[date] || 0) + v;
        });
        return { actualHours: hours, fpQty: qty };
    }, [jobCostDetail]);

    // Every Forecast_User_Data field row repeats the SQL columns, so dedupe by
    // Forecast_Row_ID before summing.
    const phasedHours = useMemo(() => {
        const seenRows = new Set();
        const out = {};
        (jobUserData || []).forEach((d) => {
            if (d?.Forecast_Tab !== 'Burn Tool Labor Phased') return;
            if (!d.Phase_Start || !d.Phase_End || seenRows.has(d.Forecast_Row_ID)) return;
            seenRows.add(d.Forecast_Row_ID);
            const code = String(d.Forecast_Row_ID || '').split('=')[0].trim();
            const daily = (num(d.Number_Hours) * (num(d.Number_Units) || 1)) / 7;
            if (!code || daily <= 0) return;
            (out[code] = out[code] || []).push({ start: dateOf(d.Phase_Start), end: dateOf(d.Phase_End), daily });
        });
        return out;
    }, [jobUserData]);

    // Scorecard rows repeat per field; the tracker's parse assigns (last row
    // wins), so mirror that rather than summing. Values arrive as the typed
    // text, commas and all.
    const trackerInstalled = useMemo(() => {
        const out = {};
        (jobUserData || []).forEach((d) => {
            if (d?.Forecast_Tab !== 'Scorecard' || d?.Forecast_Field_Name !== 'installed') return;
            const code = String(d.Forecast_Row_ID || '').trim();
            const v = num(String(d.Forecast_Value ?? '').replace(/,/g, ''));
            if (code && v > 0) out[code] = v;
        });
        return out;
    }, [jobUserData]);

    return { actualHours, fpQty, phasedHours, trackerInstalled };
};

// ── Burn Tool periods ───────────────────────────────────────────────────────
// The phased Burn Tool keys its grid rows by ISO week (code=weekMonday), so a
// phase's periods are the Monday-anchored weeks its Phase_Start–Phase_End
// interval touches. Each period lists only the days inside the interval.
const MAX_PERIOD_DAYS = 840;

export const burnPeriods = (interval) => {
    if (!interval?.start || !interval?.end || interval.start > interval.end) return [];
    const out = [];
    const byWeek = {};
    const end = moment(interval.end);
    for (let t = moment(interval.start), n = 0; !t.isAfter(end, 'day') && n < MAX_PERIOD_DAYS; t.add(1, 'days'), n += 1) {
        const wk = t.clone().startOf('isoWeek').format('YYYY-MM-DD');
        if (!byWeek[wk]) out.push((byWeek[wk] = { week: wk, days: [] }));
        byWeek[wk].days.push(t.format('YYYY-MM-DD'));
    }
    return out;
};

// A total quantity split evenly across the interval's Burn Tool periods:
// every week gets the same share, and a week's share splits across its days
// inside the interval. Returns null when there's nothing to spread.
export const evenPeriodSpread = (total, interval) => {
    const periods = burnPeriods(interval);
    if (!(total > 0) || periods.length === 0) return null;
    const periodQty = total / periods.length;
    const perDay = {};
    periods.forEach((p) => {
        p.days.forEach((d) => { perDay[d] = periodQty / p.days.length; });
    });
    return { periods, periodQty, perDay, total };
};

// The phased Burn Tool crew ranges ({start, end, daily}) flattened into one
// per-day map — the hours-mode baseline, which keeps the crew ramp the phased
// rows encode instead of spreading the total evenly. Null when nothing lands.
export const phasedDailyPlan = (ranges) => {
    const perDay = {};
    let any = false;
    (ranges || []).forEach((r) => {
        if (!r?.start || !r?.end || !(r.daily > 0)) return;
        const end = moment(r.end);
        for (let t = moment(r.start), n = 0; !t.isAfter(end, 'day') && n < MAX_PERIOD_DAYS; t.add(1, 'days'), n += 1) {
            const d = t.format('YYYY-MM-DD');
            perDay[d] = (perDay[d] || 0) + r.daily;
            any = true;
        }
    });
    return any ? perDay : null;
};

// Per-day Baseline / Estimate / Actual series for one phase's quantities —
// the same math the Work-tab Gantt draws in its B/E/A rows, in a form the
// Productivity drill-down can lay out as date columns.
//
//   baseline — budget spread evenly across the Burn Tool periods (ISO weeks),
//              or plannedByDay verbatim when the caller has a real per-day
//              plan (hours mode passes the phased crew plan)
//   estimate — run rate needed to finish the remaining budget by the interval
//              end, starting at the report date
//   actual   — installed that day; the caller folds submissions, HeavyJob FP
//              postings and any live entries into actualByDay
//
// Days span the Burn Tool interval widened to cover any install or planned
// day outside it and the report date. Returns null when there's nothing to
// plot.
const MAX_DAYS = 400;

export const phaseDailySeries = ({ interval, reportDate, budgetTotal = 0, actualByDay = {}, plannedByDay = null }) => {
    const posted = Object.keys(actualByDay).filter((d) => num(actualByDay[d]));
    const planned = plannedByDay ? Object.keys(plannedByDay).filter((d) => num(plannedByDay[d])) : [];
    const bounds = [interval?.start, interval?.end, ...posted, ...planned].filter(Boolean).sort();
    if (bounds.length === 0) return null;
    const start = bounds[0];
    const end = [bounds[bounds.length - 1], reportDate].filter(Boolean).sort().pop();

    const days = [];
    for (let t = moment(start); !t.isAfter(moment(end), 'day') && days.length < MAX_DAYS; t.add(1, 'days')) {
        days.push(t.format('YYYY-MM-DD'));
    }

    const spread = plannedByDay ? null : evenPeriodSpread(budgetTotal, interval);
    const baselineOf = (d) => (plannedByDay ? num(plannedByDay[d]) : (spread?.perDay[d] || 0));

    const actualOf = (d) => num(actualByDay[d]);
    const doneToDate = days.reduce((t, d) => t + (d <= reportDate ? actualOf(d) : 0), 0);

    const estStart = interval?.start && reportDate > interval.start ? reportDate : interval?.start;
    const remDays = interval?.end && estStart ? moment(interval.end).diff(moment(estStart), 'days') + 1 : 0;
    const estDaily = budgetTotal > 0 && remDays > 0 ? Math.max(0, budgetTotal - doneToDate) / remDays : 0;
    const estOf = (d) => (estDaily && d >= estStart && d <= interval.end ? estDaily : 0);

    return { days, baselineOf, estOf, actualOf, doneToDate, estDaily, truncated: days.length >= MAX_DAYS };
};

// On-track read for one phase: % of budget done vs % planned as of the report
// date. When the caller has a real per-day plan (the saved quantity schedule's
// curve) that drives both percentages — the plan's own total is the
// denominator, so a shaped curve stays self-consistent. Otherwise planned
// follows the even spread across the Burn Tool periods. null when there's no
// budget or no schedule to measure against. ±5 points counts as on track.
export const phaseStatus = (budgetTotal, doneToDate, interval, reportDate, plannedByDay = null) => {
    if (plannedByDay) {
        let total = 0;
        let planned = 0;
        Object.entries(plannedByDay).forEach(([d, v]) => {
            const q = num(v);
            total += q;
            if (d <= reportDate) planned += q;
        });
        if (total > 0) {
            const pctPlanned = Math.round((planned / total) * 100);
            const pctDone = Math.round((doneToDate / total) * 100);
            const diff = pctDone - pctPlanned;
            return { pctDone, pctPlanned, state: diff >= 5 ? 'ahead' : diff <= -5 ? 'behind' : 'ontrack' };
        }
    }
    if (!budgetTotal || !interval?.start || !interval?.end) return null;
    const spread = evenPeriodSpread(budgetTotal, interval);
    if (!spread) return null;
    const planned = Object.entries(spread.perDay)
        .reduce((t, [d, v]) => t + (d <= reportDate ? v : 0), 0);
    const pctPlanned = Math.round((planned / budgetTotal) * 100);
    const pctDone = Math.round((doneToDate / budgetTotal) * 100);
    const diff = pctDone - pctPlanned;
    return { pctDone, pctPlanned, state: diff >= 5 ? 'ahead' : diff <= -5 ? 'behind' : 'ontrack' };
};
